import './assets/main.css'
import 'quill/dist/quill.snow.css'
import 'flag-icons/css/flag-icons.min.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import router from './router'
import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";

import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import {definePreset} from "@primeuix/themes";

import ToastService from 'primevue/toastservice';
import BaseButton from "@/components/base/BaseButton.vue";
import Ripple from 'primevue/ripple';
import Tooltip from 'primevue/tooltip';
import ConfirmationService from 'primevue/confirmationservice';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import BaseIcon from "@/components/base/BaseIcon.vue";
import BackButton from "@/components/base/BackButton.vue";

import { useAuthStore } from '@/stores/auth'
import { i18n, initializeI18n } from '@/i18n'
import apiClient from '@/api/apiClient'

const MyPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#E7F4FE',
      100: '#CEE9FD',
      200: '#A1D7FF',
      300: '#558CFA',
      400: '#3D7AF7',
      500: '#0050F0',
      600: '#1D4ED8',
      700: '#0039B2',
      800: '#052F86',
      900: '#002478',
      950: '#001c5a'
    },
    secondary: {
      50: '#ECFEFF',
      100: '#CFFAFE',
      200: '#A5F3FC',
      300: '#7AE7FF',
      400: '#59DFFC',
      500: '#00D2FF',
      600: '#04BBE2',
      700: '#03819C',
      800: '#00647A',
      900: '#164E63'
    }
  },
  components: {
    card: {
      body: {
        padding: '1rem'
      }
    }
  }
});


const app = createApp(App)

// @ts-expect-error - devtools is not a standard property in Vue 3 app config
app.config.devtools = true
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());
app.use(pinia)

app.use(router)
app.use(i18n)
app.use(PrimeVue, {
  theme: {
    preset: MyPreset,
    options: {
      darkModeSelector: false,
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue'
      }
    }
  },
  ripple: true,
});
app.use(ToastService);
app.use(ConfirmationService);

app.directive('tooltip', Tooltip);

app.directive('ripple', Ripple);

app.component('BaseButton', BaseButton)
app.component('BaseIcon', BaseIcon)
app.component('BackButton', BackButton)
app.component('DataTable', DataTable);
app.component('Column', Column);

// Block mount until auth is initialized once on startup
const authStore = useAuthStore()

// Global 401 Interceptor for Auto-Logout
apiClient.addResponseInterceptor(async (response) => {
  if (response.status === 401) {
    // Only redirect if we thought we were authenticated.
    // This prevents redirect loops or redirecting anonymous users who just hit a protected endpoint (though the router guard should prevent that).
    if (authStore.isAuthenticated) {
      console.warn('Session expired (401). Logging out...');
      authStore.clearAuth();
      await router.push({ 
        name: 'login', 
        query: { 
          redirect: router.currentRoute.value.fullPath,
          reason: 'session_expired' // Optional: could be used to show a message on login page
        } 
      });
    }
  }
  return response;
});

await initializeI18n(app.config.globalProperties.$primevue.config)

// initializeAuth fetches the webapp config (including is_local, derived from the backend's
// IMAGE_TAG). Init Sentry before mount, but skip it entirely on local so we don't report dev errors.
await authStore.initializeAuth()

if (!authStore.config?.is_local) {
  Sentry.init({
    app,
    dsn: "https://6ace5dcbcfca347cb7698b975dbb9eeb@o1240520.ingest.us.sentry.io/4511580954492928",
    dataCollection: {
      // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
      // userInfo: false,
      // httpBodies: [],
    },
    enableLogs: true,
  });
}

app.mount('#app')
